import React, { useState, useEffect, useCallback, ChangeEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';

import { accountabilityAPI } from '@/services/accountabilityAPI';
import { PendingAppeal, Pagination } from '@/types/accountability';

import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { faCheckCircle } from '@fortawesome/free-regular-svg-icons';

import Alert from '@/components/Alert';
import Badge from '@/components/Badge';
import Card from '@/components/Card';
import LoadingState from '@/components/LoadingState';
import Page from '@/components/Page';
import Paginator from '@/components/Paginator/Paginator';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import TextStack from '@/components/TextStack';
import { Body } from '@/components/Typography';
import Button from '@/components/Button';
import Link from '@/elements/Link';
import { TableCell, TableRow } from '@/elements/Table';
import Modal from '@/components/Modal';
import TextArea from '@/components/TextArea';
import FormElement from '@/components/FormElement';
import styled from '@/styles';

const TabBar = styled('div', {
  display: 'inline-flex',
  backgroundColor: 'white',
  borderRadius: '$lg $lg 0 0',
  padding: '0 4px',
  borderBottom: '1px solid $colors$neutralLightest',
});

const TabLabel = styled('span', {
  display: 'inline-block',
  padding: '12px 24px',
  fontSize: '14px',
  fontWeight: 500,
  color: '$colors$textDefault',
  borderBottom: '2px solid $colors$themeDefault',
});

const headerFields = [
  'Worker',
  'Violation Type',
  'Shift ID',
  'Appeal Reason',
  'Days Pending',
  'Submitted',
  '',
];

type ActionModalProps = {
  type: 'approve' | 'deny';
  appeal: PendingAppeal;
  onClose: () => void;
  onSuccess: () => void;
};

const ActionModal = ({
  type,
  appeal,
  onClose,
  onSuccess,
}: ActionModalProps) => {
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (type === 'deny' && !notes.trim()) return;

    setLoading(true);
    setError(null);

    try {
      if (type === 'approve') {
        await accountabilityAPI.approveAppeal(appeal.violation_id, notes);
      } else {
        await accountabilityAPI.denyAppeal(appeal.violation_id, notes);
      }
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : `Failed to ${type} appeal`;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      disableClickout
      size="sm"
      title={type === 'approve' ? 'Approve Appeal' : 'Deny Appeal'}
      onRequestClose={onClose}
    >
      <Card.Section>
        <Body>
          {type === 'approve' ? (
            <>
              Approve appeal for <strong>{appeal.worker_name}</strong>? This
              will clear the violation and restore their profile.
            </>
          ) : (
            <>
              Deny appeal for <strong>{appeal.worker_name}</strong>? The
              violation will remain active.
            </>
          )}
        </Body>
      </Card.Section>
      <Card.Section>
        {error && (
          <Alert
            description={error}
            icon={faExclamationTriangle}
            status="danger"
            title="Error"
          />
        )}
        <FormElement
          label={type === 'approve' ? 'Notes (optional)' : 'Reason for denial'}
        >
          <TextArea
            placeholder={
              type === 'approve'
                ? 'Add optional notes...'
                : 'Provide a reason for denying this appeal.'
            }
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </FormElement>
      </Card.Section>
      <Card.Section>
        <Stack justify="end">
          <Button
            a11yLabel="Cancel"
            appearance="outline"
            label="Cancel"
            onClick={onClose}
          />
          <Button
            a11yLabel={type === 'approve' ? 'Approve' : 'Deny'}
            isLoading={loading}
            label={type === 'approve' ? 'Approve' : 'Deny'}
            status={type === 'approve' ? 'success' : 'danger'}
            onClick={handleSubmit}
            disabled={type === 'deny' && !notes.trim()}
          />
        </Stack>
      </Card.Section>
    </Modal>
  );
};

export const AppealsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [appeals, setAppeals] = useState<PendingAppeal[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [canClearViolation, setCanClearViolation] = useState(false);

  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [perPage, setPerPage] = useState(
    parseInt(searchParams.get('per_page') || '25'),
  );

  const [actionModal, setActionModal] = useState<{
    open: boolean;
    type: 'approve' | 'deny';
    appeal: PendingAppeal;
  } | null>(null);

  const updateSearchParams = useCallback(
    (newPage: number, newPerPage: number) => {
      const params = new URLSearchParams();
      params.set('page', String(newPage));
      params.set('per_page', String(newPerPage));
      setSearchParams(params);
    },
    [setSearchParams],
  );

  const loadAppeals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await accountabilityAPI.getPendingAppeals(page, perPage);

      if (response.success) {
        setAppeals(
          (response.appeals || response.data || []) as PendingAppeal[],
        );
        setPagination(response.pagination || null);
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load appeals';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [page, perPage]);

  useEffect(() => {
    loadAppeals();
  }, [loadAppeals]);

  useEffect(() => {
    let isMounted = true;

    const loadClearPermission = async () => {
      try {
        const response = await accountabilityAPI.getClearPermission();
        if (!isMounted) return;
        setCanClearViolation(
          !!(response.success && response.can_clear_violation),
        );
      } catch (err) {
        if (isMounted) {
          setCanClearViolation(false);
        }
      }
    };

    loadClearPermission();

    return () => {
      isMounted = false;
    };
  }, []);

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
    updateSearchParams(pageNumber, perPage);
  };

  const handleNumberItemsChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const newPerPage = parseInt(event.target.value);
    setPage(1);
    setPerPage(newPerPage);
    updateSearchParams(1, newPerPage);
  };

  const handleActionSuccess = () => {
    setSuccess(
      actionModal?.type === 'approve'
        ? 'Appeal approved successfully'
        : 'Appeal denied successfully',
    );
    loadAppeals();
    setActionModal(null);
  };

  const getDaysPendingBadge = (days: number) => {
    if (days >= 7) return <Badge label={`${days} days`} status="danger" />;
    if (days >= 3) return <Badge label={`${days} days`} status="warning" />;
    return <Badge label={`${days} days`} status="success" />;
  };

  const getViolationTypeBadge = (type: string) => {
    return type === 'late_drop' ? (
      <Badge label="Late Drop" status="warning" />
    ) : (
      <Badge label="No Show" status="danger" />
    );
  };

  const totalItems = pagination?.total_count || 0;

  return (
    <Page
      footer={
        appeals.length > 0 &&
        pagination && (
          <Paginator
            currentPage={page}
            handleNumberItemsChange={handleNumberItemsChange}
            handlePageChange={handlePageChange}
            itemsLength={totalItems}
            itemsPerPage={perPage}
          />
        )
      }
      size="full"
      title="Tasks"
    >
      {actionModal && (
        <ActionModal
          type={actionModal.type}
          appeal={actionModal.appeal}
          onClose={() => setActionModal(null)}
          onSuccess={handleActionSuccess}
        />
      )}

      <TabBar>
        <TabLabel>Violations</TabLabel>
      </TabBar>

      {(error || success) && (
        <Card>
          <Card.Section>
            <Stack vertical gap={8}>
              {error && (
                <Alert
                  description={error}
                  icon={faExclamationTriangle}
                  status="danger"
                  title="Error"
                />
              )}
              {success && (
                <Alert
                  description={success}
                  icon={faCheckCircle}
                  status="success"
                  title="Success"
                />
              )}
            </Stack>
          </Card.Section>
        </Card>
      )}

      <Card>
        <Table>
          <TableHeader fields={headerFields} />
          <tbody>
            {appeals.map((appeal) => (
              <TableRow key={appeal.violation_id}>
                <TableCell>
                  <TextStack spacing="tight">
                    <Body weight="medium">{appeal.worker_name}</Body>
                  </TextStack>
                </TableCell>
                <TableCell>
                  {getViolationTypeBadge(appeal.violation_type)}
                </TableCell>
                <TableCell>
                  <Link to={`/orders`}>#{appeal.job_id}</Link>
                </TableCell>
                <TableCell>
                  <Body size="sm">
                    {appeal.appeal_reason.length > 80
                      ? `${appeal.appeal_reason.substring(0, 80)}...`
                      : appeal.appeal_reason}
                  </Body>
                </TableCell>
                <TableCell>
                  {getDaysPendingBadge(appeal.days_pending)}
                </TableCell>
                <TableCell>
                  <TextStack spacing="tight">
                    <Body weight="medium">
                      {format(
                        new Date(appeal.appeal_submitted_at),
                        'MM/dd/yyyy',
                      )}
                    </Body>
                    <Body size="sm" color="light">
                      {format(new Date(appeal.appeal_submitted_at), 'h:mm a')}
                    </Body>
                  </TextStack>
                </TableCell>
                <TableCell>
                  <Stack gap={8}>
                    {canClearViolation && (
                      <Button
                        a11yLabel="Approve"
                        appearance="outline"
                        label="Approve"
                        status="success"
                        onClick={() =>
                          setActionModal({
                            open: true,
                            type: 'approve',
                            appeal,
                          })
                        }
                      />
                    )}
                    <Button
                      a11yLabel="Deny"
                      appearance="outline"
                      label="Deny"
                      status="danger"
                      onClick={() =>
                        setActionModal({
                          open: true,
                          type: 'deny',
                          appeal,
                        })
                      }
                    />
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {loading && (
              <TableRow>
                <TableCell
                  colSpan={headerFields.length}
                  style={{ textAlign: 'center' }}
                >
                  <LoadingState overlayColor="white" />
                </TableCell>
              </TableRow>
            )}
            {!loading && appeals.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={headerFields.length}
                  style={{ textAlign: 'center' }}
                >
                  <Body>No pending appeals.</Body>
                </TableCell>
              </TableRow>
            )}
          </tbody>
        </Table>
      </Card>
    </Page>
  );
};

export default AppealsPage;
